// CampaignReport.jsx  – copy / paste over the old file
import React, { useEffect, useState, useRef } from 'react';
import { Alert, Box, CircularProgress } from '@mui/material';
import { Background, BackgroundPattern, PortraitBox, NavDots, Dot } from './ReportComponents';

import OpeningSlide       from './Slides/OpeningSlide';
import InfluencersSlide   from './Slides/InfluencersSlide';
import VideosSlide        from './Slides/VideosSlide';
import ViewsChartSlide    from './Slides/ViewsChartSlide';
import ReachSlide         from './Slides/ReachSlide';
import TimelineSlide      from './Slides/TimelineSlide';

import { useQuery } from 'react-query';
import API from '../../API';
import { useParams } from 'react-router-dom';

const SLIDE_DURATION = 4000; // milliseconds

export default function CampaignReport() {
  const { campaignId } = useParams();
  const [currentSlide, setCurrentSlide] = useState(0);
  const slideCount = 6;

  const intervalRef = useRef(null);

  /** ----------------------------------------------------------------
   *  Fetch campaign report data
   * ----------------------------------------------------------------*/
  const { data, isLoading, error } = useQuery(
    ['campaign', campaignId, 'report'],
    () => API.campaigns.fetchReport(campaignId),
    { refetchOnWindowFocus: false }
  );

  /** ----------------------------------------------------------------
   *  Carousel logic
   * ----------------------------------------------------------------*/
  const startAutoAdvance = () => {
    clearInterval(intervalRef.current);
    intervalRef.current = setInterval(() => {
      setCurrentSlide((prev) => (prev + 1) % slideCount);
    }, SLIDE_DURATION);
  };

  useEffect(() => {
    if (!isLoading && data) startAutoAdvance();
    return () => clearInterval(intervalRef.current); // cleanup on unmount
  }, [isLoading, data]);

  /** Manual nav‑dot click → jump + restart timer */
  const handleDotClick = (index) => {
    setCurrentSlide(index);
    startAutoAdvance();
  };

  /** ----------------------------------------------------------------
   *  Render
   * ----------------------------------------------------------------*/
  return (
    <Background>
      <BackgroundPattern>
        {/* Loading overlay ------------------------------------------------ */}
        <Box
          sx={{
            position: 'absolute',
            inset: 0,
            zIndex: 100,
            display: isLoading ? 'flex' : 'none',
            alignItems: 'center',
            justifyContent: 'center',
            bgcolor: 'rgba(0,0,0,0.45)',
            backdropFilter: 'blur(2px)',
            transition: 'opacity .6s ease'
          }}
        >
          <CircularProgress sx={{ color: '#eee' }} />
        </Box>

        {/* Error ---------------------------------------------------------- */}
        {!!error && (
          <Alert
            severity="error"
            sx={{
              position: 'absolute',
              top: '50%',
              left: '50%',
              transform: 'translate(-50%,-50%)',
              width: '90%',
              zIndex: 101
            }}
          >
            {error?.response?.data?.error || error.message}
          </Alert>
        )}

        {/* Slides container ---------------------------------------------- */}
        <PortraitBox
          isLoaded={!isLoading && !!data}
          // onMouseEnter={() => clearInterval(intervalRef.current)} // ← optional pause on hover
          // onMouseLeave={startAutoAdvance}                         // ← optional pause on hover
        >
          <OpeningSlide     index={0} currentSlide={currentSlide} report={data} />
          <InfluencersSlide index={1} currentSlide={currentSlide} report={data} />
          <VideosSlide      index={2} currentSlide={currentSlide} report={data} />
          <ViewsChartSlide  index={3} currentSlide={currentSlide} report={data} />
          <ReachSlide       index={4} currentSlide={currentSlide} report={data} />
          <TimelineSlide    index={5} currentSlide={currentSlide} report={data} />

          {/* Nav dots ---------------------------------------------------- */}
          <NavDots>
            {Array.from({ length: slideCount }).map((_, idx) => (
              <Dot
                key={idx}
                active={currentSlide === idx}
                onClick={() => handleDotClick(idx)}
              />
            ))}
          </NavDots>
        </PortraitBox>
      </BackgroundPattern>
    </Background>
  );
}
